import React, { useRef, useState, useEffect, useCallback } from 'react';

type HoverMarqueeProps = {
  children: React.ReactNode;
  className?: string;
  speed?: number; // px/giây, mặc định 100
};

const HoverMarquee: React.FC<HoverMarqueeProps> = ({ children, className, speed = 100 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const [offset, setOffset] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [distance, setDistance] = useState(0);

  // đo lại khoảng cách cần cuộn
  const measure = useCallback(() => {
    const c = containerRef.current;
    const t = contentRef.current;
    if (!c || !t) return;
    const containerW = c.clientWidth;
    const contentW = t.scrollWidth;
    const d = Math.max(0, contentW - containerW);
    setDistance(d);
    if (d === 0) setOffset(0);
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [measure]);

  // animate khi hover
  useEffect(() => {
    if (distance === 0) return;

    let last = performance.now();

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;

      setOffset((prev) => {
        if (hovered) {
          // chạy sang trái đến -distance
          const next = prev - speed * dt;
          return Math.max(-distance, next);
        } else {
          // về 0
          const next = prev + speed * dt;
          return Math.min(0, next);
        }
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [hovered, distance, speed]);

  return (
    <div
      ref={containerRef}
      className={['relative overflow-hidden select-none', className].filter(Boolean).join(' ')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={contentRef}
        style={{
          display: 'inline-block',
          whiteSpace: 'nowrap',
          transform: `translateX(${offset}px)`,
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default HoverMarquee;

